import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { ToolOutlined, StarOutlined, HighlightOutlined, FormOutlined, FileTextOutlined, ArrowRightOutlined } from '@ant-design/icons';
import '../Legend/LegendDrawer.scss';
import { useLegendForm, useLegendUI } from '../../../context/MapLegendContext';
import { defaultLayerPropertiesState, omitNonLayerProperties, useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import { useAuth } from '../../../context/AuthContext';
import { useCustomFileMap } from '../../../hooks/useCustomFileMap';
import { LayerAction } from '../../../enums/mapPortalActions';
import { FileTypes, FileTypeExtensionDefinitions } from '../../../enums/fileTypes';
import { NotificationTypes } from '../../../enums/notificationTypes';
import { UploadFlows } from '../../../enums/uploadFlows';
import { layerActionAccess } from '../constants';
import ConditionalRender from '../../../shared/components/ConditionalRender';
import CustomWarning from '../../../shared/components/CustomWarning';
import Notification from '../../../shared/components/Notification';
import ReadOnlyFieldset from '../../../shared/components/ReadOnlyFieldset';
import FieldPropertiesTab from './FieldPropertiesTab';
import PopupsTab from './PopupsTab';
import LabelsTab from './LabelsTab';
import EffectsTab from './EffectsTab';
import LayerSettingsTab from './LayerSettingsTab';
import Symbology from './Symbology';
import LayerTitleInput from './LayerTitleInput';
import { buildSingleConfig, UNIQUE_FIELD_REQUIRED_ERROR } from './Symbology/symbologyConfig.utils';
import { validateQuantityConfig, QUANTITY_FIELD_REQUIRED_ERROR, quantityHasNoRanges } from './Symbology/quantityRange.utils';
import { validateHeatmapConfig } from './Symbology/heatmap.utils';

interface LayerPropertiesModalProps {
  mapId: string;
  setIsLayerLoading: (loading: boolean) => void;
  onLayerUpdateComplete?: () => Promise<void>;
}

const allTabIcons = [
  { icon: <ToolOutlined />, label: 'Settings' },
  { icon: <StarOutlined />, label: 'Scale' },
  { icon: <HighlightOutlined />, label: 'Highlight' },
  { icon: <FormOutlined />, label: 'Text' },
  { icon: <FileTextOutlined />, label: 'Files' },
  { icon: <ArrowRightOutlined />, label: 'Other' },
];

/**
 * Layer Properties modal (tabs, save, discard-changes guard, re-upload), opened
 * through LegendUIContext's `modalVisible` for the layer seeded into
 * LayerPropertiesContext. Shared by Map Portal's legend and CommonMap.
 */
const LayerPropertiesModal: React.FC<LayerPropertiesModalProps> = ({ mapId, setIsLayerLoading, onLayerUpdateComplete }) => {
  const [isReUpload, setIsReUpload] = useState(false);
  const [showNoSymbologyWarning, setShowNoSymbologyWarning] = useState(false);
  const [pendingSaveUploadAnother, setPendingSaveUploadAnother] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { modalVisible, setModalVisible, selectedTab, setSelectedTab, layerUploadFlow, setLayerUploadFlow, requestUploadAnother } = useLegendUI();
  const { uploadFileType, uploadedFiles, S3UniqueFolderName, folderS3Key, folderId } = useLegendForm();
  const layerPropsContext = useLayerProperitesContext();
  const { canAccess } = useAuth();
  const canEditLayer = canAccess(layerActionAccess(LayerAction.EDIT_LAYER_PROPERTIES));
  const { saveLayerToGeoserver } = useCustomFileMap({});

  // Re-Upload runs inside an already-open modal on an existing layer, so it is
  // excluded from both: it keeps the Save and Close / Cancel pair and its X.
  const isUploadCreatedLayer = layerUploadFlow !== null && !isReUpload;
  const canUploadAnother = layerUploadFlow === UploadFlows.UPLOAD_NEW && !isReUpload;

  // US-101662 Scenario 9: when the Layer Properties modal opens we snapshot
  // its initial state so Cancel / X can restore it. Comparing serialized
  // snapshots is the simplest correct way to detect dirtiness across many
  // disparate fields without a per-field allow-list. `setState` is stripped
  // because it's a function reference and isn't part of the user's edits.
  const initialSnapshotRef = useRef<string | null>(null);
  const initialContextValueRef = useRef<Record<string, unknown> | null>(null);
  // Always re-snapshot when the modal *opens* (true edge). Resetting on
  // close would race with the discard restore.
  const wasModalOpenRef = useRef(false);
  useEffect(() => {
    if (modalVisible && !wasModalOpenRef.current) {
      const snapshot = omitNonLayerProperties(layerPropsContext) as unknown as Record<string, unknown>;
      initialContextValueRef.current = snapshot;
      initialSnapshotRef.current = JSON.stringify(snapshot);
      // `selectedTab` is app-scoped, so without this the modal reopens on
      // whichever tab was last used — which may not exist for this layer
      // (a raster only exposes Settings). Re-Upload sets tab 0 itself.
      setSelectedTab(0);
    }
    wasModalOpenRef.current = modalVisible;
  }, [modalVisible, layerPropsContext, setSelectedTab]);

  const isLayerPropsDirty = (): boolean => {
    if (initialSnapshotRef.current == null) return false;
    const current = omitNonLayerProperties(layerPropsContext) as unknown as Record<string, unknown>;
    return JSON.stringify(current) !== initialSnapshotRef.current;
  };

  const closeLayerModal = () => {
    setModalVisible(false);
    setLayerUploadFlow(null);
    // Saved layers re-seed on open; an uploaded layer has nothing to re-seed from.
    if (isUploadCreatedLayer) {
      layerPropsContext.setState({ ...defaultLayerPropertiesState, setState: layerPropsContext.setState });
    }
  };

  // US-101662 Scenario 9 ("Discard Changes" option): restore the snapshot
  // and close. Used by both the Cancel button and the X / Esc close path
  // when the user confirms the discard prompt.
  const discardLayerPropsAndClose = () => {
    const snapshot = initialContextValueRef.current;
    if (snapshot) {
      layerPropsContext.setState((prev) => ({
        ...prev,
        ...(snapshot as Partial<typeof prev>),
      }));
    }
    closeLayerModal();
  };

  // Replacement for raw `closeLayerModal` on the Cancel / X path. If the
  // user has unsaved edits, surface the confirm-discard prompt; otherwise
  // close immediately (no point asking for confirmation when there's
  // nothing to discard).
  const attemptCloseLayerModal = () => {
    if (!isLayerPropsDirty()) {
      closeLayerModal();
      return;
    }
    Modal.confirm({
      title: 'You have unsaved changes. Do you want to discard them?',
      okText: 'Discard Changes',
      okButtonProps: { danger: true },
      cancelText: 'Continue Editing',
      centered: true,
      onOk: () => {
        discardLayerPropsAndClose();
      },
    });
  };

  const executeSave = async (uploadAnother = false) => {
    // Single funnel to saveLayerToGeoserver.
    if (!canEditLayer) return;
    // Layer name is required on both Settings and Symbology title inputs.
    if (!String(layerPropsContext.layerTitle ?? '').trim()) {
      Notification({ message: 'Layer name is required', type: NotificationTypes.ERROR });
      // Jump to Settings (tab 0) so the empty field is visible if the user was
      // on another tab when they hit Save.
      setSelectedTab(0);
      return;
    }

    setIsLayerLoading(true);
    setIsSaving(true);
    try {
      const actualDataLocation = S3UniqueFolderName || layerPropsContext.folderId || folderId;
      // Build the typed symbology_config — the primary write path. For single
      // we derive it from the flat fields; for unique the Unique editor keeps
      // context.symbologyConfig as the source of truth (its category array
      // can't be represented by the flat columns).
      const symbologyConfig =
        layerPropsContext.symbology === 'single' ? buildSingleConfig(layerPropsContext) : (layerPropsContext.symbologyConfig ?? null);

      // US-101670 Scenarios 4-9: block the save when the quantity config has an
      // invalid / overlapping / duplicate range. The modal stays open and the
      // exact AC message is surfaced. A type that was picked but never touched
      // leaves symbologyConfig on its previous value (or undefined), so treat
      // that as "no field selected".
      if (layerPropsContext.symbology === 'quantity') {
        const quantityError = symbologyConfig?.type === 'quantity' ? validateQuantityConfig(symbologyConfig) : QUANTITY_FIELD_REQUIRED_ERROR;
        if (quantityError) {
          Notification({ message: quantityError, type: NotificationTypes.ERROR });
          setIsLayerLoading(false);
          return;
        }
      }

      if (layerPropsContext.symbology === 'unique' && !(symbologyConfig?.type === 'unique' && symbologyConfig.field)) {
        Notification({ message: UNIQUE_FIELD_REQUIRED_ERROR, type: NotificationTypes.ERROR });
        setIsLayerLoading(false);
        return;
      }

      // US-101677 Scenarios 5 & 9: block the save on an invalid opacity or
      // min>=max zoom; the modal stays open and the AC message is shown.
      if (layerPropsContext.symbology === 'heatmap' && symbologyConfig?.type === 'heatmap') {
        const heatmapError = validateHeatmapConfig(symbologyConfig);
        if (heatmapError) {
          Notification({ message: heatmapError, type: NotificationTypes.ERROR });
          setIsLayerLoading(false);
          return;
        }
      }

      // Same reason as `isRasterLayer`: an existing layer keeps its own saved
      // format, otherwise a save would stamp it with the last upload's type.
      const effectiveSourceFormat =
        layerPropsContext.isExistingLayer && layerPropsContext.sourceFormat ? (layerPropsContext.sourceFormat as FileTypes) : uploadFileType;

      const updatedVal = {
        ...omitNonLayerProperties(layerPropsContext),
        folderId: actualDataLocation,
        uniqueLayerName: layerPropsContext.uniqueLayerName || layerPropsContext.layerTitle || 'new_layer',
        s3key: folderS3Key || '',
        sourceFormat: effectiveSourceFormat,
        symbologyConfig,
      };

      let extension = FileTypeExtensionDefinitions[effectiveSourceFormat];

      if (uploadedFiles && uploadedFiles.length > 0) {
        const shpFile = uploadedFiles.find((f: any) => f.name.toLowerCase().endsWith('.shp'));

        if (shpFile) {
          extension = '.shp';
        } else {
          const fileName = uploadedFiles[0].name;
          const lastDotIndex = fileName.lastIndexOf('.');
          if (lastDotIndex !== -1) {
            extension = fileName.substring(lastDotIndex);
          }
        }
      }

      const wasReupload = isReUpload;
      // Tab index 1 is the Symbology tab — see `renderTabContent`.
      // US-101662 Scenario 8 / 11 require exact AC wording for the
      // symbology save success / failure toasts; suppress
      // saveLayerToGeoserver's generic toasts in that case so the user
      // doesn't see two stacked messages.
      const isSymbologyTab = selectedTab === 1;
      // The upload flow only reports that processing started, so it leaves on
      // dispatch. The payload is already built, so the reset is safe here.
      if (canUploadAnother) {
        closeLayerModal();
        if (uploadAnother) requestUploadAnother();
      }
      await saveLayerToGeoserver(
        layerPropsContext.uniqueLayerName,
        mapId,
        updatedVal,
        extension,
        isReUpload,
        async () => {
          closeLayerModal();
          setTimeout(async () => {
            if (onLayerUpdateComplete) await onLayerUpdateComplete();
            setIsLayerLoading(false);
            // US-101368 Scenario 2: confirm the swap to the user. Symbology,
            // popups, layer name, sort_order, and group all survive because
            // saveLayerToGeoserver only writes the columns present in the
            // payload — the rest of the row is left as-is.
            if (wasReupload) {
              Notification({
                message: 'Dataset updated successfully',
                type: NotificationTypes.SUCCESS,
              });
              setIsReUpload(false);
            }
          }, 1000);
        },
        isSymbologyTab,
      );
      if (isSymbologyTab) {
        Notification({
          message: 'Layer symbology updated successfully',
          type: NotificationTypes.SUCCESS,
        });
      }
    } catch (error) {
      console.error(error);
      setIsLayerLoading(false);
      // US-101368 Scenario 9: a failed re-upload must leave the existing
      // dataset untouched. saveLayerToGeoserver is a single PUT that's
      // applied server-side after the worker conversion succeeds, so the
      // row is only mutated on success — we just have to surface the error.
      if (isReUpload) {
        Notification({
          message: 'Upload failed. Existing dataset remains unchanged',
          type: NotificationTypes.ERROR,
        });
      } else if (selectedTab === 1) {
        // US-101662 Scenario 11: modal stays open, unsaved config remains,
        // and the user gets exact AC wording.
        Notification({
          message: 'Unable to save symbology changes. Please try again.',
          type: NotificationTypes.ERROR,
        });
      } else if (canUploadAnother) {
        // The modal has already closed, so this is the only failure signal.
        Notification({
          message: 'Unable to save layer. Please try again.',
          type: NotificationTypes.ERROR,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewLayerSave = (uploadAnother = false) => {
    if (!String(layerPropsContext.layerTitle ?? '').trim()) {
      Notification({ message: 'Layer name is required', type: NotificationTypes.ERROR });
      setSelectedTab(0);
      return;
    }

    const symbologyConfig =
      layerPropsContext.symbology === 'single' ? buildSingleConfig(layerPropsContext) : (layerPropsContext.symbologyConfig ?? null);

    // A field is chosen but its table was left empty: that renders nothing, which
    // is a legitimate choice, so warn rather than block. Choosing no field at all
    // is still blocked in executeSave — nothing can be rendered from it.
    const uniqueConfig = symbologyConfig?.type === 'unique' ? symbologyConfig : null;
    const noSymbology =
      layerPropsContext.symbology === 'unique'
        ? !!uniqueConfig?.field && uniqueConfig.categories.length === 0
        : layerPropsContext.symbology === 'quantity' && quantityHasNoRanges(symbologyConfig);

    if (noSymbology) {
      setPendingSaveUploadAnother(uploadAnother);
      setShowNoSymbologyWarning(true);
      return;
    }

    executeSave(uploadAnother);
  };

  // Wrappers, not aliases: passing handleNewLayerSave straight to onClick would
  // hand React's MouseEvent to `uploadAnother`, which is truthy.
  const handleSaveAndClose = () => handleNewLayerSave();
  const handleSaveAndUploadAnother = () => handleNewLayerSave(true);

  // An existing layer must be judged only by its own saved type. `uploadFileType`
  // is app-scoped upload-wizard state that sticks on Raster after a TIFF upload,
  // so it may only be consulted while configuring a brand new upload.
  const isRasterLayer = layerPropsContext.isExistingLayer
    ? layerPropsContext.layerType === 'WMS' || layerPropsContext.layerType === 'raster'
    : uploadFileType === FileTypes.RASTER;

  const tabIcons = isRasterLayer ? [allTabIcons[0]] : allTabIcons;

  // Settings (0) and Symbology (1) are the tabs that carry the layer-name field.
  const showsLayerTitle = selectedTab === 0 || selectedTab === 1;

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return <LayerSettingsTab setIsReUpload={setIsReUpload} />;
      case 1:
        return <Symbology />;
      case 2:
        return <EffectsTab />;
      case 3:
        return <LabelsTab />;
      case 4:
        return <PopupsTab />;
      case 5:
        return <FieldPropertiesTab />;
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        open={modalVisible}
        onCancel={attemptCloseLayerModal}
        footer={null}
        width={600}
        maskClosable={false}
        // An uploaded layer is dismissed through the footer buttons only.
        closable={!isUploadCreatedLayer}
        keyboard={!isUploadCreatedLayer}
        className="legend-drawer__layer-modal"
        styles={{
          body: {
            maxHeight: '78vh',
            overflowY: 'auto',
          },
        }}
      >
        <div className="legend-drawer__tabs">
          {tabIcons.map((tab, index) => (
            <Button
              key={index}
              onClick={() => setSelectedTab(index)}
              className={['legend-drawer__tab-btn', selectedTab === index ? 'legend-drawer__tab-btn--active' : ''].filter(Boolean).join(' ')}
            >
              {tab.icon}
            </Button>
          ))}
        </div>
        <div className="legend-drawer__tab-content">
          {/* Action bar sits outside so Cancel stays usable. */}
          <ReadOnlyFieldset readOnly={!canEditLayer} className="legend-drawer__readonly-fields">
            {/* Sits outside the scroll region so it stays put while the tab scrolls. */}
            {showsLayerTitle && <LayerTitleInput />}
            {/* Content scrolls within the fixed-height modal; the action bar
                stays pinned at the bottom (it never scrolls out of view). */}
            <div className="legend-drawer__tab-scroll">{renderTabContent()}</div>
          </ReadOnlyFieldset>
          {/* US-101662 Scenario 8 / 9: explicit Save & Cancel pair. Cancel
              triggers the confirm-discard prompt (when dirty) before
              closing — see attemptCloseLayerModal. */}
          <ConditionalRender visible={canEditLayer}>
            <div className="legend-drawer__layer-modal-actions">
              <Button type="primary" onClick={handleSaveAndClose} loading={isSaving} className="legend-drawer__save-btn">
                Save and Close
              </Button>
              {canUploadAnother ? (
                <Button onClick={handleSaveAndUploadAnother} disabled={isSaving} className="legend-drawer__upload-another-btn">
                  Save and Upload Another
                </Button>
              ) : (
                <Button onClick={attemptCloseLayerModal} disabled={isSaving} className="legend-drawer__cancel-btn">
                  Cancel
                </Button>
              )}
            </div>
          </ConditionalRender>
        </div>
      </Modal>

      <CustomWarning
        open={showNoSymbologyWarning}
        message="There is currently no symbology configured, are you sure you want to continue?"
        confirmText={canUploadAnother ? 'Save and proceed' : 'Save and close'}
        cancelText="Go back to symbology"
        onConfirm={() => {
          setShowNoSymbologyWarning(false);
          executeSave(pendingSaveUploadAnother);
        }}
        onCancel={() => setShowNoSymbologyWarning(false)}
      />
    </>
  );
};

export default LayerPropertiesModal;
